"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var data_grid_3_wrapper_1 = require("@tableau/data-grid-3-wrapper");
var React = require("react");
var reselect_1 = require("reselect");
var shallowEqual = require("shallowequal");
var CenteredSpinner_1 = require("../CenteredSpinner");
var DataGridStyles_1 = require("../style/DataGridStyles");
var LayoutComponents_1 = require("../style/LayoutComponents");
/**
 * This component transforms the asynchronous callback-based API of the Data Grid's RowStore into a
 * "synchronous" pattern that more closely aligns with React idioms, storing the returned rows in
 * component state and re-rendering the data grid every time the stored rows are updated.
 *
 * This was originally built as a workaround for a race condition defect in the data grid (TFS 894890):
 * The data grid doesn't render measured column widths correctly with an async row store, so we
 * avoid that faulty code path by giving the data grid a row store that always returns rows synchronously.
 *
 * HOWEVER, this component now contains more functionality than simply the adapter for the RowStore.
 * It now also keeps track of loading and error states from the row store query.
 */
var DataGridWithSynchronousRowStore = /** @class */ (function (_super) {
    __extends(DataGridWithSynchronousRowStore, _super);
    function DataGridWithSynchronousRowStore() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            loadingFromNewRowStore: true,
            currentlyStoredRows: [],
        };
        _this.previouslyReportedErrors = [];
        // Keep track of the component being mounted, to avoid calling setState after unmount
        // internalIsMounted must start out as true so that the the first rowHandler callback will update
        // the state, if the backing row store happens to be synchronous.
        _this.internalIsMounted = true;
        _this.componentDidMount = function () { return _this.internalIsMounted = true; };
        _this.componentWillUnmount = function () { return _this.internalIsMounted = false; };
        // This is the getItems function that is passed as the "row store" to the actual datagrid
        _this.rowStoreGetItems = function (start, end, rowHandler) {
            _this.fetchRowsAsynchronously(start, end);
            rowHandler(_this.state.currentlyStoredRows);
        };
        _this.fetchRowsAsynchronously = function (start, end) {
            var rowStore = _this.props.rowStore;
            rowStore.getItems(start, end, function (newRows, newErrors) {
                if (!_this.internalIsMounted || rowStore !== _this.props.rowStore) {
                    return;
                }
                // Notify parent of any errors that came back. We do this here, rather than in componentDidUpdate,
                // because the latter causes the data grid not to apply the column widths.
                var hasNewErrors = newErrors && newErrors.length > 0 && !shallowEqual(newErrors, _this.previouslyReportedErrors);
                if (hasNewErrors) {
                    _this.reportNewErrors(newRows, newErrors);
                    // Notifying the parent component may cause this component to become unmounted
                    if (!_this.internalIsMounted) {
                        return;
                    }
                }
                _this.setState(function (oldState) {
                    // If we don't check for equality, we'll get stuck in an infinite loop of re-rendering the same rows.
                    // Since our PaginatedRowStore shallow-copies the data every time it returns the row array, we have to use
                    // a shallowEqual check, rather than simply reference equality.
                    var rowsHaveChanged = !shallowEqual(oldState.currentlyStoredRows, newRows);
                    if (rowsHaveChanged || hasNewErrors) {
                        return {
                            loadingFromNewRowStore: false,
                            currentlyStoredRows: rowsHaveChanged ? newRows : oldState.currentlyStoredRows,
                        };
                    }
                    else {
                        return null;
                    }
                });
            });
        };
        // Create a new row store only when the rows change. Creating a new row store will force the data grid to re-fetch,
        // so we want to do this only when there is actually new data to re-fetch (e.g. when the "async" fetch has completed)
        // but not more frequently (e.g. when the component gets re-rendered from above with no new props)
        _this.memoizedCreateRowStore = reselect_1.defaultMemoize(function (rows) {
            return { getItems: _this.rowStoreGetItems };
        });
        return _this;
    }
    DataGridWithSynchronousRowStore.prototype.reportNewErrors = function (newRows, newErrors) {
        this.previouslyReportedErrors = newErrors;
        this.props.onError(newRows, newErrors);
    };
    DataGridWithSynchronousRowStore.prototype.UNSAFE_componentWillReceiveProps = function (nextProps) {
        // When the row store from above has changed, we keep the old rows, but set the loading boolean to true.
        // This will cause the component to render a spinner but keep the old rows visible until the new rows arrive.
        if (this.props.rowStore !== nextProps.rowStore) {
            this.setState({
                loadingFromNewRowStore: true,
            });
            // Clear out the stored errors in ExplorerStyledDisplayGrid so they will be un-rendered
            // and new errors from the new row store can take their place. This is necessary because
            // there are two sources of state for errors: the rendered ErrorReporter objects and the error list
            // inside ErrorListProvider. In order to remove the errors from the ErrorListProvider, we unmount
            // the errors that are rendered by ExplorerStyledDisplayGrid, thus unregistering them from the error list.
            this.props.onError([], []);
        }
    };
    DataGridWithSynchronousRowStore.prototype.render = function () {
        var dataGridPropsWithOverrides = __assign(__assign({}, this.props), { rowStore: this.memoizedCreateRowStore(this.props.rowStore, this.state.currentlyStoredRows), config: __assign(__assign({}, this.props.config), { rememberLastScrollPos: true }) });
        var displayLoadingSpinner = this.props.showSpinnerWhileLoading && this.state.loadingFromNewRowStore;
        return (React.createElement(LayoutComponents_1.SuperimposedDivs, null,
            React.createElement(data_grid_3_wrapper_1.DataGrid, __assign({}, dataGridPropsWithOverrides, { key: 'DataGrid' })),
            displayLoadingSpinner ? React.createElement(OverlayWithSpinner, { key: 'Spinner' }) : null));
    };
    return DataGridWithSynchronousRowStore;
}(React.Component));
exports.DataGridWithSynchronousRowStore = DataGridWithSynchronousRowStore;
function OverlayWithSpinner() {
    return (React.createElement(React.Fragment, null,
        React.createElement(DataGridStyles_1.DataGridSpinnerGlass, null),
        React.createElement("div", { style: { paddingTop: 48 } },
            React.createElement(CenteredSpinner_1.CenteredSpinner, null))));
}
exports.OverlayWithSpinner = OverlayWithSpinner;
//# sourceMappingURL=DataGridWithSynchronousRowStore.js.map